import { z } from 'zod';

export const createSetupShopifyOAuthSchema = (t: (key: string) => string) =>
  z.object({
    clientId: z.string().min(1, t('shopify.validation.clientIdRequired')),
    clientSecret: z.string().min(1, t('shopify.validation.clientSecretRequired')),
    shopUrl: z.string().min(1, t('shopify.validation.shopUrlRequired')),
  });

export type SetupShopifyOAuthFormValues = z.infer<ReturnType<typeof createSetupShopifyOAuthSchema>>;

/**
 * The OAuth field accepts a bare domain (we prepend https:// before redirecting), so we can't
 * require a literal `https` prefix like the manual schema. Instead, normalize the same way the
 * redirect builder does and confirm it resolves to an http(s) origin — this rejects values like
 * `javascript:…` that would otherwise be pushed into `window.location.href`.
 */
const isHttpStoreUrl = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const { protocol } = new URL(withScheme);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

export const createSetupWooOAuthSchema = (t: (key: string) => string) =>
  z.object({
    url: z
      .string()
      .min(1, t('woocommerce.validation.shopUrlRequired'))
      .refine(isHttpStoreUrl, t('woocommerce.validation.invalidUrl')),
  });

export type SetupWooOAuthFormValues = z.infer<ReturnType<typeof createSetupWooOAuthSchema>>;

export const createSetupWooManualSchema = (t: (key: string) => string) =>
  z.object({
    url: z.string().startsWith('https', t('woocommerce.manual.urlMustBeHttps')),
    consumerKey: z.string().min(1, t('woocommerce.manual.consumerKeyRequired')),
    consumerSecret: z.string().min(1, t('woocommerce.manual.consumerSecretRequired')),
  });

export type SetupWooManualFormValues = z.infer<ReturnType<typeof createSetupWooManualSchema>>;
